import { useState } from 'react'
import { useQuery, useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Card } from '@/components/ui/card'
import { RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'
import { DateRangePicker, rangeFromPreset, type DateRange } from './DateRangePicker'
import { SubscriptionCharts } from './SubscriptionCharts'

type MetricKey =
  | 'conversions'
  | 'costPerConversion'
  | 'cost'
  | 'conversionRate'
  | 'clicks'
  | 'avgCpc'

const METRICS: Record<
  MetricKey,
  { label: string; color: string; format: (v: number) => string }
> = {
  conversions: {
    label: 'New Trials',
    color: '#D93025',
    format: (v) => formatNumber(v),
  },
  costPerConversion: {
    label: 'Cost / trial',
    color: '#1A73E8',
    format: (v) => formatCurrency(v),
  },
  cost: {
    label: 'Cost',
    color: '#F9AB00',
    format: (v) => formatCurrency(v, { compact: v >= 1000 }),
  },
  conversionRate: {
    label: 'Conv. rate',
    color: '#7C3AED',
    format: (v) => `${v.toFixed(2)}%`,
  },
  clicks: {
    label: 'Visitors',
    color: '#0891B2',
    format: (v) => formatNumber(v),
  },
  avgCpc: {
    label: 'Avg. CPC',
    color: '#DB2777',
    format: (v) => formatCurrency(v),
  },
}

const METRIC_ORDER: MetricKey[] = [
  'costPerConversion',
  'conversionRate',
  'clicks',
  'conversions',
  'cost',
  'avgCpc',
]

function formatCurrency(v: number, opts: { compact?: boolean } = {}): string {
  if (opts.compact && v >= 1000) return `$${(v / 1000).toFixed(2)}K`
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v)
}

function formatNumber(v: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v)
}

function formatDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function todayISO(): string {
  const now = new Date()
  const y = now.getUTCFullYear()
  const m = String(now.getUTCMonth() + 1).padStart(2, '0')
  const d = String(now.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function rangeIncludesToday(range: { start: string; end: string }): boolean {
  const t = todayISO()
  return range.start <= t && range.end >= t
}

type ChartTooltipProps = {
  active?: boolean
  payload?: { dataKey?: string | number; value?: number; payload?: { date: string } }[]
  label?: string
}

function ChartTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload?.length) return null
  const date = payload[0]?.payload?.date
  return (
    <div className="rounded-md border border-neutral-200 bg-white px-3 py-2 shadow-sm text-xs min-w-[180px]">
      <div className="font-medium text-neutral-900 mb-1.5">
        {date ? formatDate(date) : ''}
      </div>
      {payload.map((p) => {
        const key = p.dataKey as MetricKey
        const meta = METRICS[key]
        if (!meta) return null
        return (
          <div key={String(key)} className="flex items-center gap-2 mt-0.5">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: meta.color }}
            />
            <span className="text-neutral-600">{meta.label}</span>
            <span
              className="ml-auto font-medium"
              style={{ color: meta.color }}
            >
              {meta.format(p.value ?? 0)}
            </span>
          </div>
        )
      })}
    </div>
  )
}

type TileProps = {
  metricKey: MetricKey
  value: string
  active: boolean
  onToggle: () => void
}

function KpiTile({ metricKey, value, active, onToggle }: TileProps) {
  const meta = METRICS[metricKey]
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'group px-4 py-3 sm:px-6 sm:py-4 text-left transition-colors',
        active ? 'text-white' : 'bg-white text-neutral-900 hover:bg-neutral-50',
      )}
      style={active ? { background: meta.color } : undefined}
    >
      <div
        className={cn(
          'text-xs font-medium mb-1 flex items-center gap-1',
          active ? 'text-white/90' : 'text-neutral-600',
        )}
      >
        <span className="text-[10px]">▾</span>
        {meta.label}
      </div>
      <div className="text-xl sm:text-3xl font-medium tracking-tight truncate">{value}</div>
    </button>
  )
}

export default function AdminGoogleAdsPage() {
  const [range, setRange] = useState<DateRange>(() => {
    const r = rangeFromPreset('last_7')!
    return { ...r, label: 'Last 7 days' }
  })
  const [refreshing, setRefreshing] = useState(false)
  const [activeMetrics, setActiveMetrics] = useState<Record<MetricKey, boolean>>({
    conversions: false,
    costPerConversion: true,
    cost: false,
    conversionRate: false,
    clicks: false,
    avgCpc: false,
  })
  const data = useQuery(api.googleAds.getDashboard, {
    startDate: range.start,
    endDate: range.end,
  })
  const refresh = useAction(api.googleAdsActions.refreshNow)

  const onRefresh = async () => {
    setRefreshing(true)
    try {
      await refresh({ days: 30 })
    } catch (err) {
      console.error('[GoogleAds] refresh failed', err)
    } finally {
      setRefreshing(false)
    }
  }

  const toggleMetric = (key: MetricKey) => {
    setActiveMetrics((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      if (!Object.values(next).some(Boolean)) return prev
      return next
    })
  }

  const isLoading = data === undefined
  const hasData = data?.hasData ?? false

  const chartData = (data?.series ?? []).map((d) => ({
    date: d.date,
    label: formatDate(d.date),
    conversions: d.conversions,
    costPerConversion: d.costPerConversion,
    cost: d.cost,
    conversionRate: (d as { conversionRate?: number }).conversionRate ?? 0,
    clicks: (d as { clicks?: number }).clicks ?? 0,
    avgCpc: d.avgCpc,
  }))

  const totals = data?.totals as
    | {
        conversions: number
        costPerConversion: number
        cost: number
        conversionRate?: number
        clicks: number
        avgCpc: number
      }
    | undefined

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="space-y-3">
          <h1 className="text-2xl font-bold text-neutral-900">Conversion Funnel</h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
              Refresh
            </button>
            {data?.lastFetched && (
              <span className="text-xs text-neutral-400">
                Last refreshed {new Date(data.lastFetched).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 sm:self-start">
          <span className="hidden md:inline-block text-base font-medium text-neutral-700">
            {data ? `${formatDate(data.startDate)} – ${formatDate(data.endDate)}` : '—'}
          </span>
          <DateRangePicker value={range} onChange={setRange} size="lg" />
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="grid grid-cols-3 sm:grid-cols-6 border-b border-neutral-200 [&>*]:border-r [&>*]:border-b [&>*]:border-neutral-200 [&>*:nth-child(3n)]:border-r-0 sm:[&>*:nth-child(3n)]:border-r sm:[&>*:nth-child(6n)]:border-r-0 [&>*:nth-last-child(-n+3)]:border-b-0 sm:[&>*]:border-b-0">
          <KpiTile
            metricKey="costPerConversion"
            value={isLoading ? '—' : formatCurrency(totals?.costPerConversion ?? 0)}
            active={activeMetrics.costPerConversion}
            onToggle={() => toggleMetric('costPerConversion')}
          />
          <KpiTile
            metricKey="conversionRate"
            value={isLoading ? '—' : `${(totals?.conversionRate ?? 0).toFixed(2)}%`}
            active={activeMetrics.conversionRate}
            onToggle={() => toggleMetric('conversionRate')}
          />
          <KpiTile
            metricKey="clicks"
            value={isLoading ? '—' : formatNumber(totals?.clicks ?? 0)}
            active={activeMetrics.clicks}
            onToggle={() => toggleMetric('clicks')}
          />
          <KpiTile
            metricKey="conversions"
            value={isLoading ? '—' : formatNumber(totals?.conversions ?? 0)}
            active={activeMetrics.conversions}
            onToggle={() => toggleMetric('conversions')}
          />
          <KpiTile
            metricKey="cost"
            value={
              isLoading
                ? '—'
                : formatCurrency(totals?.cost ?? 0, { compact: (totals?.cost ?? 0) >= 1000 })
            }
            active={activeMetrics.cost}
            onToggle={() => toggleMetric('cost')}
          />
          <KpiTile
            metricKey="avgCpc"
            value={isLoading ? '—' : formatCurrency(totals?.avgCpc ?? 0)}
            active={activeMetrics.avgCpc}
            onToggle={() => toggleMetric('avgCpc')}
          />
        </div>

        <div className="h-[360px] p-4">
          {isLoading ? (
            <div className="h-full flex items-center justify-center text-sm text-neutral-400">
              Loading…
            </div>
          ) : !hasData ? (
            <div className="h-full flex items-center justify-center flex-col gap-2 text-sm text-neutral-400">
              <span>No data yet.</span>
              <span className="text-xs">
                {rangeIncludesToday(range)
                  ? 'Please wait up to 24 hours for Google Ads data to update.'
                  : 'Set the Google Ads env vars in Convex prod, then click Refresh.'}
              </span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 12, right: 24, left: 4, bottom: 0 }}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                  minTickGap={24}
                />
                {METRIC_ORDER.map((key, i) => {
                  if (!activeMetrics[key]) return null
                  const orientation = i % 2 === 0 ? 'left' : 'right'
                  const tickFormatter = (v: number) => {
                    if (key === 'conversionRate') return `${v.toFixed(0)}%`
                    if (key === 'cost' || key === 'costPerConversion' || key === 'avgCpc')
                      return `$${v.toFixed(0)}`
                    return `${v.toFixed(0)}`
                  }
                  return (
                    <YAxis
                      key={key}
                      yAxisId={key}
                      orientation={orientation}
                      tickFormatter={tickFormatter}
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                      width={48}
                    />
                  )
                })}
                <Tooltip content={<ChartTooltip />} />
                {METRIC_ORDER.map((key) => {
                  if (!activeMetrics[key]) return null
                  return (
                    <Line
                      key={key}
                      yAxisId={key}
                      type="linear"
                      dataKey={key}
                      stroke={METRICS[key].color}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  )
                })}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      <SubscriptionCharts />
    </div>
  )
}
